import i18next from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RemoteParticipant } from 'twilio-video';
import { call } from '@/services/call';
import { logger } from '@/services/logger';
import { selectRoomParticipants, selectRoomPinned } from '@/store/features/room/selectors';
import { addParticipant, removeParticipant } from '@/store/features/room/slice';
import { showUIAlert } from '@/store/features/ui/slice';
import { AlertActions, AlertTypes } from '@/store/features/ui/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { isParticipantMediaEnabled } from '@/utils/track';

export function useRoom() {
  const { id: roomId } = useParams<{ id: string }>();

  const participantSids = useAppSelector(
    selectRoomParticipants,
    shallowEqual,
  );

  const pinned = useAppSelector(selectRoomPinned);

  const dispatch = useAppDispatch();

  useEffect(() => {
    call.attachLocalVideo();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      call.destroy();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleParticipantConnected = useCallback((participant: RemoteParticipant) => {
    const isAudioEnabled = isParticipantMediaEnabled(participant.tracks, 'audio');
    const isVideoEnabled = isParticipantMediaEnabled(participant.tracks, 'video');

    dispatch(
      addParticipant({
        sid: participant.sid,
        identity: participant.identity,
        isAudioEnabled,
        isVideoEnabled,
      })
    );

    call.sendUserDataMessage();
  }, [dispatch]);

  const handleParticipantDisconnected = useCallback((participant: RemoteParticipant) => {
    dispatch(removeParticipant(participant.sid));
  }, [dispatch]);

  useEffect(() => {
    if (!roomId) {
      return;
    }

    const joinRoom = async () => {
      try {
        if (!call.room) {
          throw new Error('Missing token');
        }

        call.room.participants.forEach((participant) => {
          handleParticipantConnected(participant);
        });

        call.onNewParticipant(handleParticipantConnected);
        call.onParticipantDisconnected(handleParticipantDisconnected);
      } catch (error) {
        logger.error(error);
        dispatch(
          showUIAlert({
            title: i18next.t('error'),
            message: i18next.t('something_went_wrong'),
            type: AlertTypes.error,
            action: AlertActions.navigateHome,
          }),
        );
      }
    };

    joinRoom();
  }, [dispatch, handleParticipantConnected, handleParticipantDisconnected, roomId]);

  const totalParticipants = useMemo(() => participantSids.length + 1, [participantSids.length]);

  const isLocalAbsolute = useMemo(() => participantSids.length === 1, [participantSids.length]);

  const hasPinned = useMemo(() => !!pinned, [pinned]);

  return {totalParticipants, isLocalAbsolute, participantSids, hasPinned};
}